import { WELL_TOLD_SCRIPT_URL } from "@/constants/redemption";
import type { CustomLocation, WellToldAutocompleteCtor } from "@/interfaces/redemption";

/**
 * On-demand loader for Well Told's hometown-autocomplete widget (their UMD script, same one
 * v1 loaded). Injects it once and resolves with the `WellToldAutocomplete` constructor — or
 * `null` when it can't load, so the picker can show a retry message instead of a dead field.
 * The widget reuses `window.google.maps.places` when present; callers load the Places SDK
 * first (`loadGooglePlaces`) so it never injects a second, non-async Maps script.
 */

declare global {
  interface Window {
    WellToldAutocomplete?: WellToldAutocompleteCtor;
  }
}

const SCRIPT_ID = "well-told-autocomplete";
let loader: Promise<WellToldAutocompleteCtor | null> | null = null;

export function loadWellToldAutocomplete(): Promise<WellToldAutocompleteCtor | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.WellToldAutocomplete) return Promise.resolve(window.WellToldAutocomplete);
  if (loader) return loader;

  loader = new Promise((resolve) => {
    const settle = () => resolve(window.WellToldAutocomplete ?? null);
    const fail = () => {
      loader = null; // allow a later retry
      resolve(null);
    };
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", settle);
      existing.addEventListener("error", fail);
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = WELL_TOLD_SCRIPT_URL;
    s.addEventListener("load", settle);
    s.addEventListener("error", fail);
    document.head.appendChild(s);
  });
  return loader;
}

/**
 * The Well Told photography key for a product + chosen colour: `{color}` → the colour label
 * with spaces as dashes (v1: `typeWithColor.replace('{color}', colorVal.replace(' ', '-'))`).
 * A colour-dependent key with no colour chosen yet has no preview → null.
 */
export function buildPreviewKey(
  previewKey: string | undefined,
  colorLabel: string | undefined,
): string | null {
  if (!previewKey) return null;
  if (!previewKey.includes("{color}")) return previewKey;
  const color = colorLabel?.trim();
  return color ? previewKey.replace("{color}", color.replace(/\s+/g, "-")) : null;
}

/** "lat,lng" as v1 stored it in `contacts.coords` (and Shopify's `_coords` property). */
export function formatCoords(lat: number, lng: number): string {
  return `${lat},${lng}`;
}

/** The review-card row for a chosen custom location: its label + the value to print. */
export function describeCustomLocation(location: CustomLocation): {
  label: string;
  value: string;
} {
  return location.mode === "state"
    ? { label: "Map state", value: location.state }
    : { label: "Custom location", value: location.hometown };
}
